/** @jsx jsx */
import React, { useCallback } from 'react';
import { styled, css, jsx } from '@compiled/react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { DragHandleButtonSmall } from '@atlaskit/pragmatic-drag-and-drop-react-accessibility/drag-handle-button-small';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/IssueCreateProvider.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useDraggableRow } from '../../../../common/ui/draggable-row/index.tsx';
import { useGroupBy } from '../../../../controllers/group-by/index.tsx';
import { useHierarchyStore } from '../../../../controllers/hierarchy/hierarchy-store/index.tsx';
import { useReparentModalStore } from '../../../../controllers/reparent-modal/index.tsx';
import { useRankIssueActions } from '../../../../controllers/use-rank-issue-actions/index.tsx';
import { useRankIssue } from '../../../../controllers/use-rank-issue/index.tsx';
import { useHierarchyLevel } from '../../../../services/hierarchy-level/index.tsx';
import messages from './messages.tsx';

export const DRAG_HANDLE_TEST_ID = 'business-list.ui.list-view.select-row-cell.drag-handle.handle';
export const DRAG_HANDLE_CONTAINER_TEST_ID =
	'business-list.ui.list-view.select-row-cell.drag-handle.drag-handle-container';

type Props = {
	issueId: string;
	parentIssueId?: string;
	hierarchyLevel?: number;
};

export function DragHandle({ issueId, parentIssueId, hierarchyLevel: issueHierarchyLevel }: Props) {
	const {
		disallowDragWithMessage,
		isDragHandleHidden,
		dragHandleRef,
		onDrag: handleDrag,
		onDragTargetKeyDown: handleDragTargetKeyDown,
		onContainerKeyDown: handleContainerKeyDown,
	} = useDraggableRow();
	const [{ issuesMap, hierarchyData }] = useHierarchyStore();
	const currentIssues = parentIssueId ? issuesMap.get(parentIssueId)?.children : hierarchyData.data;
	const currentIssueIndex = currentIssues?.findIndex((issue) => issue.issueId === Number(issueId));
	const { loading } = useRankIssue();
	const { rankFirst, rankLast, rankBefore, rankAfter } = useRankIssueActions(
		issueId,
		parentIssueId,
	);
	const isOptimisticItem = isOptimisticIssue(Number(issueId));
	const [groupByFieldId] = useGroupBy();
	const isGrouping = groupByFieldId != null;
	const { isFiltering } = useFilteredViewContextInput({
		view: 'list',
	});
	const [{ hierarchyLevel }] = useHierarchyLevel();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { openReparentModal }] = useReparentModalStore();
	const { formatMessage } = useIntl();

	const handleOpenReparentModal = useCallback(() => {
		openReparentModal(String(issueId));
		fireUIAnalytics(createAnalyticsEvent({}), 'reparent clicked', 'dragHandleDropdown');
	}, [createAnalyticsEvent, issueId, openReparentModal]);

	if (disallowDragWithMessage != null) {
		return (
			<Tooltip content={disallowDragWithMessage}>
				<DragHandleContainer
					onKeyDown={handleContainerKeyDown}
					data-testid={DRAG_HANDLE_CONTAINER_TEST_ID}
				>
					<DragHandleButtonSmall
						// Inline style because the drag handle button does not handle disabled states
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						css={css({
							cursor: 'not-allowed',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							display: isDragHandleHidden ? 'none' : 'flex',
						})}
						ref={dragHandleRef}
						data-drag-handle
						label={formatMessage(messages.reorder)}
						aria-disabled="true"
						onKeyDown={handleDragTargetKeyDown}
					/>
				</DragHandleContainer>
			</Tooltip>
		);
	}

	if (isOptimisticItem) {
		return <Handle disallowDrag data-drag-handle data-testid={DRAG_HANDLE_TEST_ID} />;
	}

	return (
		<DragHandleContainer onKeyDown={handleContainerKeyDown}>
			<DropdownMenu
				trigger={({ triggerRef, ...triggerProps }) => (
					<DragHandleButtonSmall
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
						css={css({ display: isDragHandleHidden ? 'none' : 'flex' })}
						ref={mergeRefs(dragHandleRef, triggerRef)}
						data-drag-handle
						label={formatMessage(messages.reorder)}
						{...triggerProps}
						onDrag={handleDrag}
						onKeyDown={handleDragTargetKeyDown}
					/>
				)}
			>
				<DropdownItemGroup>
					{!loading && currentIssueIndex != null && currentIssueIndex > 0 && (
						<>
							<DropdownItem onClick={rankFirst}>{formatMessage(messages.movetoTop)}</DropdownItem>
							<DropdownItem onClick={rankBefore}>{formatMessage(messages.moveUp)}</DropdownItem>
						</>
					)}
					{!loading && currentIssues && currentIssueIndex !== currentIssues.length - 1 && (
						<>
							<DropdownItem onClick={rankAfter}>{formatMessage(messages.moveDown)}</DropdownItem>
							<DropdownItem onClick={rankLast}>{formatMessage(messages.moveToBottom)}</DropdownItem>
						</>
					)}
					{!loading && !isFiltering && !isGrouping && issueHierarchyLevel !== hierarchyLevel && (
						<DropdownItem onClick={handleOpenReparentModal}>
							{parentIssueId != null
								? formatMessage(messages.changeParent)
								: formatMessage(messages.addParent)}
						</DropdownItem>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
		</DragHandleContainer>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragHandleContainer = styled.div({
	position: 'absolute',
	paddingLeft: token('space.025'),
	left: token('space.100'),
	top: token('space.0'),
	bottom: token('space.0'),
	display: 'flex',
	alignItems: 'center',
	'&::before': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtlest', colors.N300),
		width: '20px',
		display: 'flex',
		justifyContent: 'center',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Handle = styled.div<{ disallowDrag?: boolean }>({
	display: 'none',
	position: 'absolute',
	left: token('space.025'),
	top: token('space.0'),
	bottom: token('space.0'),
	'&::before': {
		content: '⠿',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		cursor: ({ disallowDrag }) => (disallowDrag ? 'not-allowed' : 'grab'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtlest', colors.N300),
		width: '20px',
		display: 'flex',
		justifyContent: 'center',
		height: '100%',
		alignItems: 'center',
	},
});
